<template>
    <div>
        <pc-loader v-bind:loading="changingDeadlines">
            <pc-capsule
                v-bind="capsuleBinder"
                v-bind:title="deadlinesCapsuleTitle"
                v-bind:has-body="canSetDeadline"
                data-test-role="deadlines-capsule"
                has-table
                v-on="capsuleListeners">
                <template v-slot:capsule-table>
                    <div
                        v-if="isAdminPage"
                        class="d-flex align-items-center mb-3">
                        <pc-checkbox
                            id="includeSatisfied"
                            v-model="showFinished"
                            v-bind:label="includeSatisfiedDeadlinesLabel"
                            class="ms-4" />
                    </div>
                    <pc-table
                        id="pending-deadlines-table"
                        v-bind:items="filteredDeadlines"
                        v-bind:columns="columns"
                        v-bind:initial-sort="initialSort"
                        v-bind:show-actions-per-item="showDeadlineActions"
                        v-bind:empty-text="emptyTable"
                        v-bind:card-property-cutoff="2"
                        key-prop="PublicId"
                        hide-search>
                        <template v-slot:DueDate="deadline">
                            <div v-if="deadline && deadline.DueDate">
                                {{ deadline.DueDate.Display }}
                            </div>
                        </template>
                        <template v-slot:TimeLeft="deadline">
                            <div v-if="deadline && deadline.TimeLeft">
                                {{ deadline.TimeLeft.Display }}
                            </div>
                        </template>
                        <template v-slot:TimePaused="deadline">
                            <div v-if="deadline && deadline.TimePaused">
                                {{ deadline.TimePaused.Display }}
                            </div>
                        </template>
                        <template v-slot:StatusDescription="deadline">
                            <div>
                                <!--
                                use text-nowrap because this will always be a short, non-user-supplied
                                string and otherwise in Firefox sometimes this breaks strangely mid-word
                            -->
                                <span
                                    class="text-nowrap"
                                    v-bind:class="getDeadlineStatusClass(deadline)">
                                    {{ deadline.StatusDescription }}
                                </span>
                            </div>
                        </template>
                        <template v-slot:OriginalDueDate="deadline">
                            <div>
                                {{ getOriginalDueDateText(deadline) }}
                            </div>
                        </template>
                        <template v-slot:SatisfiedDate="deadline">
                            <div>
                                {{ getSatisfiedOnText(deadline) }}
                            </div>
                        </template>
                        <template v-slot:actionsRow="deadline">
                            <deadline-action-row
                                v-bind:deadline="deadline"
                                v-on:update-due-date="updateDueDate"
                                v-on:update-end-date="updateEndDate"
                                v-on:update-satisfied="updateSatisfied"
                                v-on:pause-deadline="pauseDeadline"
                                v-on:resume-deadline="resumeDeadline" />
                        </template>
                    </pc-table>
                </template>
                <template v-slot:default>
                    <div v-if="!showSetDeadlineControls">
                        <pc-button
                            id="SetDeadlineBtn"
                            link
                            class="p-0"
                            v-on:click="setNewDeadline">
                            {{ deadlineToSetLabel }}
                        </pc-button>
                    </div>
                    <div v-else>
                        <div class="row">
                            <div
                                class="d-flex align-items-center"
                                v-bind:class="responsiveClasses.setDeadlineSelect">
                                <pc-select
                                    v-if="hasDeadlineDefinitions"
                                    id="DeadlineSelector"
                                    v-model="deadlineDefinitionToAdd"
                                    v-bind:options="deadlineDefinitionOptions"
                                    v-bind:label="setDeadlineLabel"
                                    text-prop="Name"
                                    value-prop="PublicId" />
                                <div
                                    v-else
                                    class="fst-italic">
                                    {{ noDeadlinesAvailableLabel }}
                                </div>
                            </div>
                            <div class="col d-flex align-items-end justify-content-end">
                                <pc-button
                                    id="AddDeadlineCancel"
                                    v-on:click="setNewDeadlineCancel">
                                    {{ cancelText }}
                                </pc-button>
                                <pc-button
                                    v-if="hasDeadlineDefinitions"
                                    id="AddDeadlineSave"
                                    v-bind:disabled="!deadlineDefinitionToAdd"
                                    primary
                                    class="ms-2"
                                    v-on:click="setNewDeadlineOk">
                                    {{ saveText }}
                                </pc-button>
                            </div>
                        </div>
                    </div>
                </template>
            </pc-capsule>
        </pc-loader>
    </div>
</template>
<script>
    import capsuleBase, { WIDE_BP } from "./_sharedCapsuleBase";
    import deadlineActionRow from "./deadlineActionRow.vue";
    import { DEADLINE_ACTIVITY_STATUS_ID } from "Util/clientDeadline";
    import { KEYS, NAMESPACE } from "./i18n/deadlinesCapsule.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { mapI18n } from "Util/i18n";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        setDeadlineSelect: {
            col: { xs: 12, [WIDE_BP.name]: 6 },
            mb: { xs: 2, [WIDE_BP.name]: 0 }
        }
    };

    // Initial sort by Status asc
    const initialSort = [[2, "asc"]];

    export default {
        components: {
            deadlineActionRow
        },
        extends: capsuleBase,
        props: {
            id: { type: String, required: false, default: "deadlines" },
            title: { type: String, required: false, default: "Deadlines" },
            model: { type: Object, required: true },
            usesDeadlineAutomations: { type: Boolean, required: true, default: false }
        },
        data() {
            return {
                deadlines: [],
                initialSort: initialSort,
                emptyTable: this.isAdminPage ? this.noDeadlinesAdminText : this.noDeadlinesNonAdminText,
                deadlineDefinitions: null,
                deadlineDefinitionToAdd: null,
                showFinished: false,
                changingDeadlines: false,
                showSetDeadlineControls: false
            };
        },
        computed: {
            ...mapI18n({ ...KEYS }, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            columns() {
                const deadlineNameColumn = {
                    header: this.deadlineNameHeader,
                    property: "DeadlineName"
                };
                const dueDateColumn = {
                    header: this.dueDateHeader,
                    property: "DueDate",
                    sortValue(item) {
                        return item.DueDate.Ticks;
                    }
                };
                const statusDescColumn = {
                    header: this.statusDescriptionHeader,
                    property: "StatusDescription"
                };
                const timeLeftColumn = {
                    header: this.timeLeftHeader,
                    property: "TimeLeft",
                    sortValue(item) {
                        return item.TimeLeft == null ? 0 : item.TimeLeft.TimeSpanInSeconds;
                    }
                };
                const timePausedColumn = {
                    header: this.timePausedHeader,
                    property: "TimePaused",
                    sortValue(item) {
                        return item.TimePaused.TimeSpanInSeconds;
                    }
                };
                const satisfiedDateColumn = {
                    header: this.satisfiedDateHeader,
                    property: "SatisfiedDate",
                    sortValue(item) {
                        return item.SatisfiedDate == null ? 0 : item.SatisfiedDate.Ticks;
                    }
                };

                const cols = this.isAdminPage
                    ? this.usesDeadlineAutomations
                        ? [
                              deadlineNameColumn,
                              dueDateColumn,
                              timeLeftColumn,
                              timePausedColumn,
                              statusDescColumn,
                              satisfiedDateColumn
                          ]
                        : [deadlineNameColumn, dueDateColumn, statusDescColumn, satisfiedDateColumn]
                    : [deadlineNameColumn, dueDateColumn, statusDescColumn];

                return cols.map((c) => {
                    c.orderable = true;
                    c.searchable = true;
                    c.visible = true;
                    return c;
                });
            },
            deadlineDefinitionOptions() {
                if (this.deadlineDefinitions) {
                    return this.deadlineDefinitions;
                }
                return [];
            },
            hasDeadlineDefinitions() {
                return this.deadlineDefinitions && this.deadlineDefinitions.length > 0;
            },
            filteredDeadlines() {
                return this.deadlines.filter((d) => {
                    return (
                        d.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.running ||
                        d.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.paused ||
                        (d.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.finished && this.showFinished)
                    );
                });
            },
            canSetDeadline() {
                return this.isAdminPage && this.$app.LoggedInRole.RolePrivileges.ProjectAdminSetDeadline;
            },
            controller() {
                return this.$serviceControllers.Deadlines;
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            }
        },
        created() {
            this._loadTableData();
        },
        methods: {
            showDeadlineActions(deadline) {
                if (!this.isAdminPage) {
                    return false;
                }
                if (deadline.DueDate && this.$app.LoggedInRole.RolePrivileges.ProjectAdminChangeDeadlineDate) {
                    return true;
                }
                return this.$app.LoggedInRole.RolePrivileges.ProjectAdminSatisfyDeadline;
            },
            async _loadTableData() {
                let result;
                if (this.isAdminPage) {
                    result = await this.controller.GetAllProjectDeadlines({
                        programPublicId: this.ProgramId,
                        projectPublicId: this.ProjectId
                    });
                    if (result.status === "success") {
                        this.deadlines = result.Data;
                    }
                } else {
                    result = await this.controller.GetPendingDeadlines({
                        programPublicId: this.ProgramId,
                        projectPublicId: this.ProjectId
                    });
                    if (result.status === "success") {
                        this.deadlines = result.Data.deadlinesList;
                    }
                }
            },
            async setNewDeadline() {
                // If we haven't fetched the deadline definitions, fetch them now
                if (!this.deadlineDefinitions) {
                    const result = await this.controller.GetDeadlineDefinitions({
                        programId: this.ProgramId
                    });
                    this.deadlineDefinitions = result.Data;
                }
                if (this.deadlineDefinitions.length > 0) {
                    this.deadlineDefinitionToAdd = this.deadlineDefinitions[0].PublicId;
                }
                this.showSetDeadlineControls = true;
            },
            async setNewDeadlineOk() {
                this.changingDeadlines = true;
                try {
                    if (!this.deadlineDefinitionToAdd) {
                        return;
                    }

                    const result = await this.controller.SetNewDeadline({
                        programId: this.ProgramId,
                        projectId: this.ProjectId,
                        deadlineDefinitionId: this.deadlineDefinitionToAdd
                    });
                    if (result.status === "success") {
                        if (result.Data) {
                            this.addOrUpdateDeadlineInList(result.Data);
                        } else {
                            this.$app.showErrorDialog(this.unableToCreateDeadlineMessage, result.error);
                        }
                    }

                    this.showSetDeadlineControls = false;
                } finally {
                    this.changingDeadlines = false;
                }
            },
            setNewDeadlineCancel() {
                this.showSetDeadlineControls = false;
            },
            async updateDueDate(deadline, dueDateStr) {
                this.changingDeadlines = true;
                try {
                    const result = await this.controller.UpdateDeadlineDueDate2({
                        programId: this.ProgramId,
                        deadlineId: deadline.PublicId,
                        newDueDateString: dueDateStr
                    });
                    if (result.status === "success") {
                        this.addOrUpdateDeadlineInList(result.Data);
                    }
                } finally {
                    this.changingDeadlines = false;
                }
            },
            async updateEndDate(deadline, endDateStr) {
                this.changingDeadlines = true;
                try {
                    const result = await this.controller.UpdateDeadlineEndDate2({
                        programId: this.ProgramId,
                        deadlineId: deadline.PublicId,
                        newEndDateString: endDateStr
                    });
                    if (result.status === "success") {
                        this.addOrUpdateDeadlineInList(result.Data);
                    }
                } finally {
                    this.changingDeadlines = false;
                }
            },
            async pauseDeadline(deadline) {
                this.changingDeadlines = true;
                try {
                    const result = await this.controller.PauseDeadline({
                        programId: this.ProgramId,
                        deadlineId: deadline.PublicId
                    });
                    if (result.status === "success") {
                        this.addOrUpdateDeadlineInList(result.Data);
                    }
                } finally {
                    this.changingDeadlines = false;
                }
            },
            async resumeDeadline(deadline) {
                this.changingDeadlines = true;
                try {
                    const result = await this.controller.ResumeDeadline({
                        programId: this.ProgramId,
                        deadlineId: deadline.PublicId
                    });
                    if (result.status === "success") {
                        this.addOrUpdateDeadlineInList(result.Data);
                    }
                } finally {
                    this.changingDeadlines = false;
                }
            },
            async updateSatisfied(deadline) {
                this.changingDeadlines = true;
                try {
                    const markSatisfied =
                        deadline.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.finished ? false : true;

                    const satisfiedStr = markSatisfied ? this.satisfiedLabel : this.unsatisfiedLabel;

                    const okResult = "OK";
                    const cancelResult = "CANCEL";
                    const dialogResult = await this.$app.showDialog({
                        title: `${this.markDeadlineDlgTitle}${satisfiedStr}`,
                        message: `${this.confirmDeadlineStatusChangeMessage}${satisfiedStr}?`,
                        buttons: [
                            {
                                text: this.cancelText,
                                result: cancelResult
                            },
                            {
                                text: this.okText,
                                primary: true,
                                result: okResult
                            }
                        ]
                    });
                    if (dialogResult === okResult) {
                        const result = await this.controller.UpdateDeadlineSatisfied({
                            programId: this.ProgramId,
                            deadlineId: deadline.PublicId,
                            satisfied: markSatisfied
                        });
                        if (result.status === "success") {
                            this.addOrUpdateDeadlineInList(result.Data);
                        }
                    }
                } finally {
                    this.changingDeadlines = false;
                }
            },
            getOriginalDueDateText(deadline) {
                return deadline.OriginalDueDate != null ? `${deadline.OriginalDueDate.Display}` : "";
            },
            getSatisfiedOnText(deadline) {
                return deadline.SatisfiedDate != null ? `${deadline.SatisfiedDate.Display}` : "";
            },
            getDeadlineStatusClass(deadline) {
                /* eslint-disable */
                return deadline.ActivityStatus === DEADLINE_ACTIVITY_STATUS_ID.finished
                    ? deadline.IsExpired
                        ? "text-bubble-danger"
                        : "text-bubble-success"
                    : deadline.Status === DEADLINE_ACTIVITY_STATUS_ID.paused
                      ? "text-bubble-danger"
                      : "text-bubble-info";
                /* eslint-enable */
            },
            addOrUpdateDeadlineInList(newOrUpdatedDeadline) {
                this.deadlines = this.deadlines.filter((d) => {
                    return d.PublicId != newOrUpdatedDeadline.PublicId;
                });
                this.deadlines.push(newOrUpdatedDeadline);
            }
        }
    };
</script>
